import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { rideAPI, driverAPI, vehicleAPI, statsAPI, notificationAPI, carouselAPI, trafficAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { 
  Car, MapPin, Bell, User, LogOut, Users, LayoutDashboard, ClipboardList,
  Truck, Plus, CheckCircle, XCircle, AlertCircle, Loader2, Image, Trash2, Home, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Thirugoo Logo Component
const ThirugooLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="/thirugoo-logo.png" 
    alt="Thirugoo" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [rides, setRides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [trafficUpdate, setTrafficUpdate] = useState(null);
  const [trafficMessage, setTrafficMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [fareEstimate, setFareEstimate] = useState('');

  // Vehicle form state
  const [vehicleForm, setVehicleForm] = useState({
    make: '',
    model: '',
    number_plate: '',
    fuel_type: 'Petrol',
    vehicle_type: 'Sedan',
    color: '',
    seats: 4
  });
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);

  // Driver form state
  const [driverDialogOpen, setDriverDialogOpen] = useState(false);
  const [driverForm, setDriverForm] = useState({
    name: '',
    phone: '',
    experience_years: 1,
    music_preference: 'None',
    food_offering: 'None',
    vehicle_id: ''
  });

  // Carousel upload state
  const [carouselDialogOpen, setCarouselDialogOpen] = useState(false);
  const [carouselForm, setCarouselForm] = useState({
    title: '',
    description: '',
    image_base64: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate(user.role === 'driver' ? '/driver' : '/customer');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [statsRes, ridesRes, driversRes, vehiclesRes, notifRes, unreadRes, carouselRes, trafficRes] = await Promise.all([
        statsAPI.getDashboard(),
        rideAPI.getAll(),
        driverAPI.getAll(),
        vehicleAPI.getAll(),
        notificationAPI.getByUser(user.id),
        notificationAPI.getUnreadCount(user.id),
        carouselAPI.getAll(),
        trafficAPI.get()
      ]);
      setStats(statsRes.data);
      setRides(ridesRes.data);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
      setCarouselImages(carouselRes.data);
      if (trafficRes.data && trafficRes.data.message) {
        setTrafficUpdate(trafficRes.data);
        setTrafficMessage(trafficRes.data.message);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSaveTrafficUpdate = async () => {
    if (!trafficMessage.trim()) {
      toast.error('Please enter a traffic message');
      return;
    }
    setLoading(true);
    try {
      await trafficAPI.create(trafficMessage.trim(), user.id);
      toast.success('Traffic update saved!');
      loadData();
    } catch (error) {
      toast.error('Failed to save traffic update');
    } finally {
      setLoading(false);
    }
  };

  const handleClearTrafficUpdate = async () => {
    if (trafficUpdate?.id) {
      try {
        await trafficAPI.delete(trafficUpdate.id);
        setTrafficUpdate(null);
        setTrafficMessage('');
        toast.success('Traffic update cleared');
      } catch (error) {
        toast.error('Failed to clear traffic update');
      }
    } else {
      setTrafficMessage('');
    }
  };

  const handleAssignRide = async () => {
    if (!selectedDriver || !selectedVehicle || !fareEstimate) {
      toast.error('Please fill all fields');
      return;
    }
    setLoading(true);
    try {
      await rideAPI.assign(selectedRide.id, selectedDriver, selectedVehicle, parseFloat(fareEstimate));
      toast.success('Ride assigned successfully!');
      setAssignDialogOpen(false);
      setSelectedRide(null);
      setSelectedDriver('');
      setSelectedVehicle('');
      setFareEstimate('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign ride');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRide = async (rideId) => {
    try {
      await rideAPI.cancel(rideId);
      toast.success('Ride cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
  };

  const handleAddVehicle = async () => {
    if (!vehicleForm.make || !vehicleForm.model || !vehicleForm.number_plate) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await vehicleAPI.create(vehicleForm);
      toast.success('Vehicle added successfully!');
      setVehicleDialogOpen(false);
      setVehicleForm({
        make: '',
        model: '',
        number_plate: '',
        fuel_type: 'Petrol',
        vehicle_type: 'Sedan',
        color: '',
        seats: 4
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDriver = async () => {
    if (!driverForm.name || !driverForm.phone) {
      toast.error('Please fill name and phone number');
      return;
    }
    if (driverForm.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      // First register the user as a driver
      const userRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: driverForm.phone,
          name: driverForm.name,
          role: 'driver'
        })
      });
      
      if (!userRes.ok) {
        const error = await userRes.json();
        throw new Error(error.detail || 'Failed to create user');
      }
      
      const userData = await userRes.json();
      
      // Then create driver profile
      await driverAPI.create({
        user_id: userData.id,
        experience_years: parseInt(driverForm.experience_years) || 1,
        music_preference: driverForm.music_preference,
        food_offering: driverForm.food_offering,
        vehicle_id: driverForm.vehicle_id || null
      });
      
      toast.success('Driver added successfully!');
      setDriverDialogOpen(false);
      setDriverForm({
        name: '',
        phone: '',
        experience_years: 1,
        music_preference: 'None',
        food_offering: 'None',
        vehicle_id: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to add driver');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    try {
      await vehicleAPI.delete(vehicleId);
      toast.success('Vehicle deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const handleCarouselImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setCarouselForm({ ...carouselForm, image_base64: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddCarouselImage = async () => {
    if (!carouselForm.image_base64) {
      toast.error('Please select an image');
      return;
    }
    setLoading(true);
    try {
      await carouselAPI.create(carouselForm, user.id);
      toast.success('Carousel image added!');
      setCarouselDialogOpen(false);
      setCarouselForm({ title: '', description: '', image_base64: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add image');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCarouselImage = async (imageId) => {
    try {
      await carouselAPI.delete(imageId);
      toast.success('Image deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      started: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status.toUpperCase()}</Badge>;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const pendingRides = rides.filter(r => r.status === 'pending');
  const activeRides = rides.filter(r => ['assigned', 'started'].includes(r.status));
  const availableDrivers = drivers.filter(d => d.is_available);
  const availableVehicles = vehicles.filter(v => v.is_available);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ThirugooLogo className="h-9 w-9" />
            <span className="text-xl font-bold text-gray-900">Thirugoo Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setActiveTab('notifications')}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">Manage rides, drivers, and vehicles</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="rides" className="flex items-center gap-2 relative">
              <ClipboardList className="h-4 w-4" />
              <span className="hidden sm:inline">Rides</span>
              {pendingRides.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                  {pendingRides.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="drivers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Drivers</span>
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="flex items-center gap-2">
              <Truck className="h-4 w-4" />
              <span className="hidden sm:inline">Vehicles</span>
            </TabsTrigger>
            <TabsTrigger value="carousel" className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              <span className="hidden sm:inline">Carousel</span>
            </TabsTrigger>
            <TabsTrigger value="traffic" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Traffic</span>
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-yellow-50 border-yellow-200">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-yellow-600">{stats?.pending_rides || 0}</p>
                  <p className="text-sm text-yellow-700">Pending Rides</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-blue-600">{stats?.active_rides || 0}</p>
                  <p className="text-sm text-blue-700">Active Rides</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-green-600">{stats?.completed_rides || 0}</p>
                  <p className="text-sm text-green-700">Completed</p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50 border-purple-200">
                <CardContent className="p-4">
                  <p className="text-3xl font-bold text-purple-600">{stats?.total_customers || 0}</p>
                  <p className="text-sm text-purple-700">Customers</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-amber-600" />
                    Drivers Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-700">{stats?.total_drivers || 0}</p>
                      <p className="text-sm text-gray-500">Total</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{stats?.available_drivers || 0}</p>
                      <p className="text-sm text-gray-500">Available</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="h-5 w-5 text-amber-600" />
                    Vehicles Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-700">{stats?.total_vehicles || 0}</p>
                      <p className="text-sm text-gray-500">Total</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{stats?.available_vehicles || 0}</p>
                      <p className="text-sm text-gray-500">Available</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Pending Rides */}
            {pendingRides.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-yellow-600">
                    <AlertCircle className="h-5 w-5" />
                    Pending Rides - Need Assignment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingRides.slice(0, 3).map((ride) => (
                      <div key={ride.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div>
                          <p className="font-medium">{ride.customer_name}</p>
                          <p className="text-sm text-gray-600">{ride.pickup_location} → {ride.drop_location}</p>
                        </div>
                        <Button 
                          size="sm" 
                          className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => {
                            setSelectedRide(ride);
                            setFareEstimate(ride.fare_estimate?.toString() || '150');
                            setAssignDialogOpen(true);
                          }}
                        >
                          Assign
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Rides Tab */}
          <TabsContent value="rides">
            <Card>
              <CardHeader>
                <CardTitle>All Rides</CardTitle>
                <CardDescription>Manage and assign rides to drivers</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Customer</TableHead>
                        <TableHead>Route</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Driver</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Fare</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rides.map((ride) => (
                        <TableRow key={ride.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{ride.customer_name}</p>
                              <p className="text-xs text-gray-500">{ride.customer_phone}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs">
                              <p className="text-sm truncate">{ride.pickup_location}</p>
                              <p className="text-xs text-gray-500 truncate">→ {ride.drop_location}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {format(new Date(ride.scheduled_time), 'PP')}
                            <br />
                            <span className="text-xs text-gray-500">{format(new Date(ride.scheduled_time), 'p')}</span>
                          </TableCell>
                          <TableCell>
                            {ride.driver_name || <span className="text-gray-400">-</span>}
                          </TableCell>
                          <TableCell>{getStatusBadge(ride.status)}</TableCell>
                          <TableCell className="font-medium">Rs.{ride.fare_estimate?.toFixed(0) || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {ride.status === 'pending' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    className="bg-amber-600 hover:bg-amber-700"
                                    onClick={() => {
                                      setSelectedRide(ride);
                                      setFareEstimate(ride.fare_estimate?.toString() || '150');
                                      setAssignDialogOpen(true);
                                    }}
                                  >
                                    Assign
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="destructive"
                                    onClick={() => handleCancelRide(ride.id)}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {ride.status === 'assigned' && (
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleCancelRide(ride.id)}
                                >
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Drivers Tab */}
          <TabsContent value="drivers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Drivers</CardTitle>
                  <CardDescription>View and manage driver profiles</CardDescription>
                </div>
                <Dialog open={driverDialogOpen} onOpenChange={setDriverDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="h-4 w-4 mr-2" /> Add Driver
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Driver</DialogTitle>
                      <DialogDescription>Enter driver details to register a new driver</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input 
                          placeholder="Driver's full name" 
                          value={driverForm.name}
                          onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone Number *</Label>
                        <Input 
                          placeholder="10-digit mobile number" 
                          value={driverForm.phone}
                          onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                          maxLength={10}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Experience (Years)</Label>
                          <Input 
                            type="number" 
                            min={0} 
                            max={50}
                            value={driverForm.experience_years}
                            onChange={(e) => setDriverForm({ ...driverForm, experience_years: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Assign Vehicle</Label>
                          <Select 
                            value={driverForm.vehicle_id} 
                            onValueChange={(v) => setDriverForm({ ...driverForm, vehicle_id: v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select vehicle" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">None</SelectItem>
                              {availableVehicles.map((v) => (
                                <SelectItem key={v.id} value={v.id}>
                                  {v.make} {v.model} - {v.number_plate}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Music Preference</Label>
                          <Select 
                            value={driverForm.music_preference} 
                            onValueChange={(v) => setDriverForm({ ...driverForm, music_preference: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="None">None</SelectItem>
                              <SelectItem value="FM">FM Radio</SelectItem>
                              <SelectItem value="Jazz">Jazz</SelectItem>
                              <SelectItem value="Rock">Rock</SelectItem>
                              <SelectItem value="Kannada">Kannada</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Food Offering</Label>
                          <Select 
                            value={driverForm.food_offering} 
                            onValueChange={(v) => setDriverForm({ ...driverForm, food_offering: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="None">None</SelectItem>
                              <SelectItem value="Water Bottle">Water Bottle</SelectItem>
                              <SelectItem value="Sandwich">Sandwich</SelectItem>
                              <SelectItem value="Both">Both</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button 
                        onClick={handleAddDriver} 
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Driver'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {drivers.map((driver) => (
                    <Card key={driver.id} className={`border-2 ${driver.is_available ? 'border-green-200' : 'border-gray-200'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                              <User className="h-6 w-6 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-semibold">{driver.name}</p>
                              <p className="text-sm text-gray-500">{driver.phone}</p>
                            </div>
                          </div>
                          <Badge className={driver.is_available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {driver.is_available ? 'Online' : 'Offline'}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center text-sm">
                          <div className="bg-gray-50 p-2 rounded">
                            <p className="font-bold text-amber-600">{driver.rating?.toFixed(1)}</p>
                            <p className="text-xs text-gray-500">Rating</p>
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <p className="font-bold text-blue-600">{driver.total_trips}</p>
                            <p className="text-xs text-gray-500">Trips</p>
                          </div>
                          <div className="bg-gray-50 p-2 rounded">
                            <p className="font-bold text-green-600">{driver.experience_years}y</p>
                            <p className="text-xs text-gray-500">Exp</p>
                          </div>
                        </div>
                        {driver.vehicle_info && (
                          <div className="mt-3 p-2 bg-blue-50 rounded text-sm">
                            <Car className="h-4 w-4 inline mr-1 text-blue-600" />
                            {driver.vehicle_info.make} {driver.vehicle_info.model} - {driver.vehicle_info.number_plate}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Vehicles</CardTitle>
                  <CardDescription>Manage your fleet</CardDescription>
                </div>
                <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="h-4 w-4 mr-2" /> Add Vehicle
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Vehicle</DialogTitle>
                      <DialogDescription>Enter vehicle details</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Make *</Label>
                          <Input 
                            placeholder="e.g., Maruti" 
                            value={vehicleForm.make}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Model *</Label>
                          <Input 
                            placeholder="e.g., Swift Dzire" 
                            value={vehicleForm.model}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Number Plate *</Label>
                        <Input 
                          placeholder="e.g., KA01AB1234" 
                          value={vehicleForm.number_plate}
                          onChange={(e) => setVehicleForm({ ...vehicleForm, number_plate: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Fuel Type</Label>
                          <Select 
                            value={vehicleForm.fuel_type} 
                            onValueChange={(v) => setVehicleForm({ ...vehicleForm, fuel_type: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Petrol">Petrol</SelectItem>
                              <SelectItem value="Diesel">Diesel</SelectItem>
                              <SelectItem value="CNG">CNG</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Vehicle Type</Label>
                          <Select 
                            value={vehicleForm.vehicle_type} 
                            onValueChange={(v) => setVehicleForm({ ...vehicleForm, vehicle_type: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Hatchback">Hatchback</SelectItem>
                              <SelectItem value="Sedan">Sedan</SelectItem>
                              <SelectItem value="SUV">SUV</SelectItem>
                              <SelectItem value="Luxury">Luxury</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Color</Label>
                          <Input 
                            placeholder="e.g., White" 
                            value={vehicleForm.color}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Seats</Label>
                          <Input 
                            type="number" 
                            min={2} 
                            max={8}
                            value={vehicleForm.seats}
                            onChange={(e) => setVehicleForm({ ...vehicleForm, seats: parseInt(e.target.value) })}
                          />
                        </div>
                      </div>
                      <Button 
                        onClick={handleAddVehicle} 
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Vehicle'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {vehicles.map((vehicle) => (
                    <Card key={vehicle.id} className={`border-2 ${vehicle.is_available ? 'border-green-200' : 'border-red-200'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold">{vehicle.make} {vehicle.model}</p>
                            <p className="text-sm text-gray-500">{vehicle.number_plate}</p>
                          </div>
                          <Badge className={vehicle.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {vehicle.is_available ? 'Available' : 'In Use'}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <Badge variant="outline">{vehicle.vehicle_type}</Badge>
                          <Badge variant="outline">{vehicle.fuel_type}</Badge>
                          <Badge variant="outline">{vehicle.seats} seats</Badge>
                          {vehicle.color && <Badge variant="outline">{vehicle.color}</Badge>}
                        </div>
                        <Button 
                          variant="destructive" 
                          size="sm" 
                          className="w-full"
                          onClick={() => handleDeleteVehicle(vehicle.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Remove
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Carousel Tab */}
          <TabsContent value="carousel">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Carousel Images</CardTitle>
                  <CardDescription>Manage homepage carousel</CardDescription>
                </div>
                <Dialog open={carouselDialogOpen} onOpenChange={setCarouselDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700">
                      <Plus className="h-4 w-4 mr-2" /> Add Image
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Carousel Image</DialogTitle>
                      <DialogDescription>Upload an image for the homepage carousel</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Image *</Label>
                        <Input 
                          type="file" 
                          accept="image/*"
                          onChange={handleCarouselImageUpload}
                        />
                      </div>
                      {carouselForm.image_base64 && (
                        <div className="w-full h-40 bg-gray-100 rounded-lg overflow-hidden">
                          <img 
                            src={`data:image/jpeg;base64,${carouselForm.image_base64}`} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input 
                          placeholder="Image title" 
                          value={carouselForm.title}
                          onChange={(e) => setCarouselForm({ ...carouselForm, title: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input 
                          placeholder="Brief description" 
                          value={carouselForm.description}
                          onChange={(e) => setCarouselForm({ ...carouselForm, description: e.target.value })}
                        />
                      </div>
                      <Button 
                        onClick={handleAddCarouselImage} 
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        disabled={loading || !carouselForm.image_base64}
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Upload Image'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {carouselImages.length === 0 ? (
                  <div className="text-center py-12">
                    <Image className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No carousel images yet</p>
                    <p className="text-sm text-gray-400">Add images to display on the homepage</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-3 gap-4">
                    {carouselImages.map((image) => (
                      <Card key={image.id} className="overflow-hidden">
                        <div className="h-40 bg-gray-100">
                          <img 
                            src={`data:image/jpeg;base64,${image.image_base64}`} 
                            alt={image.title || 'Carousel'} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardContent className="p-3">
                          <p className="font-medium truncate">{image.title || 'Untitled'}</p>
                          <p className="text-sm text-gray-500 truncate">{image.description || 'No description'}</p>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="w-full mt-2"
                            onClick={() => handleDeleteCarouselImage(image.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Remove
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Traffic Updates Tab */}
          <TabsContent value="traffic">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  Traffic Updates
                </CardTitle>
                <CardDescription>
                  Add important traffic updates that will appear as a scrolling ribbon on the landing page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Traffic Update Message</Label>
                  <Textarea
                    placeholder="Enter traffic update message (e.g., 'Road closed on MG Road due to construction. Use alternate route via Ring Road.')"
                    value={trafficMessage}
                    onChange={(e) => setTrafficMessage(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-gray-500">
                    This message will scroll at the bottom of the landing page with a red "Important Traffic Updates" banner.
                  </p>
                </div>

                {trafficUpdate && trafficUpdate.message && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-medium text-red-800 mb-1">Current Active Message:</p>
                    <p className="text-sm text-red-700">{trafficUpdate.message}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={handleSaveTrafficUpdate}
                    className="bg-amber-600 hover:bg-amber-700"
                    disabled={loading || !trafficMessage.trim()}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & Publish'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleClearTrafficUpdate}
                    disabled={!trafficMessage && !trafficUpdate}
                  >
                    Clear Update
                  </Button>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Preview:</h4>
                  {trafficMessage ? (
                    <div className="bg-red-600 text-white py-2 px-4 rounded overflow-hidden">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        <span className="font-medium">Important Traffic Updates:</span>
                        <span>{trafficMessage}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No message to preview. Enter a message above.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Back to Home Button */}
        <div className="mt-6 text-center">
          <Button variant="outline" onClick={() => navigate('/')}>
            <Home className="mr-2 h-4 w-4" /> Back to Home
          </Button>
        </div>
      </main>

      {/* Assign Ride Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Driver & Vehicle</DialogTitle>
            <DialogDescription>
              {selectedRide && (
                <span>
                  Ride from {selectedRide.pickup_location} to {selectedRide.drop_location}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Driver *</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a driver" />
                </SelectTrigger>
                <SelectContent>
                  {availableDrivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name} ({driver.phone})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableDrivers.length === 0 && (
                <p className="text-sm text-red-500">No drivers available</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Select Vehicle *</Label>
              <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {availableVehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      {vehicle.make} {vehicle.model} - {vehicle.number_plate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableVehicles.length === 0 && (
                <p className="text-sm text-red-500">No vehicles available</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Fare Estimate (Rs.) *</Label>
              <Input 
                type="number" 
                placeholder="Enter fare" 
                value={fareEstimate}
                onChange={(e) => setFareEstimate(e.target.value)}
              />
            </div>
            <Button 
              onClick={handleAssignRide} 
              className="w-full bg-amber-600 hover:bg-amber-700"
              disabled={loading || !selectedDriver || !selectedVehicle || !fareEstimate}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Assign Ride'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
